import { useId, useRef, type ReactNode } from 'react';
import {
  ArrowPathIcon,
  CameraIcon,
  PhotoIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from './Button';
import { Callout } from './Callout';
import { Input } from './Input';
import { MediaFrame } from './MediaFrame';
import { ProgressMeter } from './ProgressMeter';

// ═══════════════════════════════════════════════════════════════════════════
// PhotoInput — a set of photos with upload state (P10; stories 5-10c, 5-10f, 5-6)
// ═══════════════════════════════════════════════════════════════════════════
//
// The control for "attach photos to this thing": two ways in (the camera and the
// library), a preview and a caption per photo, and — because the bytes travel
// over a phone connection — an honest per-photo state with the controls that
// state actually earns (cancel while it is going, retry when it failed, replace
// or remove once it is there).
//
// ── Why this is a PRIMITIVE and not a capture component ───────────────────
// Because nothing in it is about inspections. It holds no ids, fetches nothing,
// compresses nothing and decides nothing about requirements: every item's state
// is handed to it and every verb is a callback. That is what lets the same
// control serve 5-10c's answer evidence, 5-10f's signature photo fallback and
// 5-6's photo-of-the-fix without three near-identical copies — and it is what
// D44/G-22 mean by "compose the system or extend it, never a one-off".
//
// ── Why it is not a `DynamicFormField` type ───────────────────────────────
// `DynamicFormField` renders a control from a static field SPEC; it has no place
// to put live upload state, and no way to express "this one is 40% uploaded and
// that one failed with this sentence". Folding photos into it would mean giving
// every text box an upload lifecycle. So `DynamicFormField` stays the renderer
// for value-typed answers and this is the renderer for evidence-typed ones —
// two primitives, one boundary, no switch statement in either.
//
// ── a11y ──────────────────────────────────────────────────────────────────
//   * The two file inputs are visually hidden but REAL and focusable-by-proxy:
//     each is triggered by a Button that carries the accessible name, so the
//     keyboard path is Tab → Enter, not a click handler on a div.
//   * The photo list is a `<ul>` and each item's controls name the photo they
//     act on ("Remove Cracked flashing"), because five "Remove" buttons in a row
//     are indistinguishable in a screen-reader's control list.
//   * Upload progress is the DS `ProgressMeter` (role=progressbar with a spoken
//     value), and every state CHANGE also lands in a `role="status"` region, so
//     a person who cannot see the bar still hears "uploaded" and "failed".
//   * Every control is ≥48px tall — this is a phone control first (P05 §9.2).

/** What one photo is doing right now. */
export type PhotoInputStatus = 'uploading' | 'stored' | 'failed';

export interface PhotoInputItem {
  /** Stable across the whole lifecycle of one photo, including its retries. */
  id: string;
  /** The photo's name, used in control labels. Falls back to its position. */
  name?: string | null;
  caption?: string | null;
  /** A data URI or object URL. Absent while a preview could not be produced. */
  previewUrl?: string | null;
  status: PhotoInputStatus;
  /** 0–100, while `status` is `uploading`. */
  progress?: number;
  /** The sentence explaining a failure — the SERVER's, never the transport's (G-27). */
  message?: string | null;
  /** e.g. "820 KB — compressed from 4.1 MB". Rendered verbatim. */
  sizeLabel?: string | null;
}

export interface PhotoInputProps {
  /** The question or thing these photos belong to. Names the whole group. */
  label: string;
  description?: ReactNode;
  items: PhotoInputItem[];
  /** Turns off every verb — a read-only or locked surface. */
  disabled?: boolean;
  /** Renders the required marker. Satisfaction is the caller's to decide. */
  required?: boolean;
  /** Hides the add controls once this many photos are attached. */
  max?: number;
  /** `accept` for the file inputs. */
  accept?: string;
  /** The limits, in words, under the add controls. */
  helpText?: ReactNode;
  /**
   * Whether the camera can be opened at all. `denied` renders the explanation
   * instead of a button that would do nothing — a picker the OS will not open is
   * worse than no picker, because the person cannot tell which of them failed.
   */
  cameraState?: 'available' | 'denied' | 'unavailable';
  /** The explanation shown when `cameraState` is `denied`. */
  cameraMessage?: ReactNode;
  onPick: (files: File[], source: 'camera' | 'library') => void;
  /** Fired when the picker closed with nothing chosen. */
  onPickCancelled?: (source: 'camera' | 'library') => void;
  onCancel?: (id: string) => void;
  onRetry?: (id: string) => void;
  onRemove?: (id: string) => void;
  onReplace?: (id: string, file: File) => void;
  onCaptionChange?: (id: string, caption: string) => void;
  /** Fired when a caption edit is finished (blur) — the moment to persist. */
  onCaptionCommit?: (id: string, caption: string) => void;
  className?: string;
  'data-testid'?: string;
}

const STATUS_LABEL: Record<PhotoInputStatus, string> = {
  uploading: 'Uploading',
  stored: 'Attached',
  failed: 'Not attached',
};

export function PhotoInput({
  label,
  description,
  items,
  disabled = false,
  required = false,
  max,
  accept = 'image/*',
  helpText,
  cameraState = 'available',
  cameraMessage,
  onPick,
  onPickCancelled,
  onCancel,
  onRetry,
  onRemove,
  onReplace,
  onCaptionChange,
  onCaptionCommit,
  className = '',
  'data-testid': testId = 'photo-input',
}: PhotoInputProps): JSX.Element {
  const groupId = useId();
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const replaceRef = useRef<HTMLInputElement>(null);
  const replacing = useRef<string | null>(null);

  const full = typeof max === 'number' && items.length >= max;
  const stored = items.filter((item) => item.status === 'stored').length;

  // A picker that closes with nothing chosen fires `change` with an empty list —
  // that IS the cancellation signal, and it is the only one the platform gives.
  // Reported rather than ignored so a caller can say "no photo was taken, your
  // answer is unchanged" instead of leaving a person wondering (P05 §9.3).
  const handlePicked = (source: 'camera' | 'library') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Reset first: picking the SAME file twice in a row fires no change event
    // unless the input's value is cleared, and "retake the same photo" is a
    // thing people do.
    event.target.value = '';
    if (files.length === 0) {
      onPickCancelled?.(source);
      return;
    }
    onPick(files, source);
  };

  const handleReplaced = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = (event.target.files ?? [])[0];
    const id = replacing.current;
    event.target.value = '';
    replacing.current = null;
    if (!file || !id) return;
    onReplace?.(id, file);
  };

  const nameOf = (item: PhotoInputItem, index: number) => item.name || item.caption || `Photo ${index + 1}`;

  return (
    <div className={`space-y-3 ${className}`} data-testid={testId}>
      <div>
        <span className="text-sm font-medium text-foreground" id={`${groupId}-label`}>
          {label}
          {required && (
            <span className="ml-1 text-destructive" aria-hidden="true">
              *
            </span>
          )}
          {required && <span className="sr-only"> (required)</span>}
        </span>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      </div>

      {/* The two ways in. Separate controls, not one "Add photo" that guesses:
          on a phone they open different things, and a person on a ladder needs
          the camera without a menu in the way. */}
      {!disabled && !full && (
        <div className="flex flex-wrap gap-2" role="group" aria-labelledby={`${groupId}-label`}>
          {cameraState !== 'unavailable' && (
            <Button
              type="button"
              variant="secondary"
              className="min-h-touch"
              icon={<CameraIcon className="h-5 w-5" />}
              onClick={() => cameraRef.current?.click()}
              disabled={cameraState === 'denied'}
              data-testid={`${testId}-camera`}
            >
              Take a photo
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            className="min-h-touch"
            icon={<PhotoIcon className="h-5 w-5" />}
            onClick={() => libraryRef.current?.click()}
            data-testid={`${testId}-library`}
          >
            Choose from library
          </Button>
        </div>
      )}

      {cameraState === 'denied' && !disabled && (
        <Callout tone="warning" title="The camera is blocked" data-testid={`${testId}-camera-denied`}>
          {cameraMessage ??
            'This browser is not allowing camera access, so a photo cannot be taken here. Allow the camera in your browser settings, or choose a photo from your library instead — nothing you have entered is lost.'}
        </Callout>
      )}

      {full && !disabled && (
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-full`}>
          {max} photos is the most this takes. Remove one to add another.
        </p>
      )}

      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}

      {/* The file inputs. Visually hidden, never `display:none` and never
          `disabled` — a hidden-but-present input is what `.click()` needs, and
          the Buttons above are the real, named, focusable controls. */}
      <input
        ref={cameraRef}
        type="file"
        accept={accept}
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handlePicked('camera')}
        data-testid={`${testId}-camera-input`}
      />
      <input
        ref={libraryRef}
        type="file"
        accept={accept}
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handlePicked('library')}
        data-testid={`${testId}-library-input`}
      />
      <input
        ref={replaceRef}
        type="file"
        accept={accept}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleReplaced}
        data-testid={`${testId}-replace-input`}
      />

      {/* One live region for the whole group: five photos each announcing
          themselves would be noise, and what a person needs to hear is that the
          set changed. */}
      <p className="sr-only" role="status" data-testid={`${testId}-status`}>
        {items.length === 0
          ? `No photos attached to ${label}.`
          : `${stored} of ${items.length} photos attached to ${label}.`}
      </p>

      {items.length > 0 && (
        <ul className="space-y-3" data-testid={`${testId}-list`}>
          {items.map((item, index) => {
            const name = nameOf(item, index);
            return (
              <li
                key={item.id}
                className="rounded-lg border border-border p-3"
                data-testid={`${testId}-item`}
                data-item-id={item.id}
                data-status={item.status}
              >
                <div className="flex gap-3">
                  <div className="w-24 shrink-0">
                    <MediaFrame ratio="1 / 1" className="!p-1">
                      {item.previewUrl ? (
                        <img
                          src={item.previewUrl}
                          alt={item.caption ? `${name}: ${item.caption}` : name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-muted">
                          <PhotoIcon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
                        </div>
                      )}
                    </MediaFrame>
                  </div>

                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="min-w-0 truncate text-xs font-medium text-foreground">{name}</span>
                      <span
                        className={`shrink-0 text-label font-semibold ${
                          item.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'
                        }`}
                        data-testid={`${testId}-item-status`}
                      >
                        {STATUS_LABEL[item.status]}
                      </span>
                    </div>

                    {item.sizeLabel && (
                      <p
                        className="text-label text-muted-foreground"
                        data-testid={`${testId}-item-size`}
                      >
                        {item.sizeLabel}
                      </p>
                    )}

                    {item.status === 'uploading' && (
                      <ProgressMeter
                        size="sm"
                        value={Math.round(item.progress ?? 0)}
                        total={100}
                        label={`Uploading ${name}`}
                        valueText={`${Math.round(item.progress ?? 0)}% uploaded`}
                      />
                    )}

                    {item.status === 'failed' && item.message && (
                      <p className="text-xs text-destructive" data-testid={`${testId}-item-message`}>
                        {item.message}
                      </p>
                    )}

                    {item.status === 'stored' && !disabled && onCaptionChange && (
                      <Input
                        size="sm"
                        className="min-h-touch"
                        value={item.caption ?? ''}
                        placeholder="Add a caption (optional)"
                        aria-label={`Caption for ${name}`}
                        onChange={(event) => onCaptionChange(item.id, event.target.value)}
                        onBlur={(event) => onCaptionCommit?.(item.id, event.target.value)}
                        data-testid={`${testId}-item-caption`}
                      />
                    )}
                    {item.status === 'stored' && disabled && item.caption && (
                      <p className="text-xs text-muted-foreground">{item.caption}</p>
                    )}

                    {!disabled && (
                      <div className="flex flex-wrap gap-2">
                        {item.status === 'uploading' && onCancel && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="min-h-touch"
                            icon={<XMarkIcon className="h-4 w-4" />}
                            onClick={() => onCancel(item.id)}
                            data-testid={`${testId}-item-cancel`}
                          >
                            <span aria-hidden="true">Cancel</span>
                            <span className="sr-only">Cancel uploading {name}</span>
                          </Button>
                        )}
                        {item.status === 'failed' && onRetry && (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="min-h-touch"
                            icon={<ArrowPathIcon className="h-4 w-4" />}
                            onClick={() => onRetry(item.id)}
                            data-testid={`${testId}-item-retry`}
                          >
                            <span aria-hidden="true">Retry</span>
                            <span className="sr-only">Retry uploading {name}</span>
                          </Button>
                        )}
                        {item.status !== 'uploading' && onReplace && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="min-h-touch"
                            icon={<CameraIcon className="h-4 w-4" />}
                            onClick={() => {
                              replacing.current = item.id;
                              replaceRef.current?.click();
                            }}
                            data-testid={`${testId}-item-replace`}
                          >
                            <span aria-hidden="true">Replace</span>
                            <span className="sr-only">Replace {name}</span>
                          </Button>
                        )}
                        {onRemove && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="min-h-touch"
                            icon={<TrashIcon className="h-4 w-4" />}
                            onClick={() => onRemove(item.id)}
                            data-testid={`${testId}-item-remove`}
                          >
                            <span aria-hidden="true">Remove</span>
                            <span className="sr-only">Remove {name}</span>
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
